import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { QuoteBuilder, QuoteViewer } from '../components/QuoteBuilder';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MessagesPage = () => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [showQuoteBuilder, setShowQuoteBuilder] = useState(false);
  const [viewingQuoteId, setViewingQuoteId] = useState(null);
  const [quotes, setQuotes] = useState([]);

  useEffect(() => {
    fetchConversations();
  }, []);

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation.id);
      fetchQuotes(selectedConversation.id);
    }
  }, [selectedConversation]);

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API}/conversations`);
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (conversationId) => {
    try {
      const response = await axios.get(`${API}/conversations/${conversationId}/messages`);
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
      toast.error('Failed to load messages');
    }
  };

  const fetchQuotes = async (conversationId) => {
    try {
      const response = await axios.get(`${API}/conversations/${conversationId}/quotes`);
      setQuotes(response.data.quotes || []);
    } catch (error) {
      console.error('Failed to fetch quotes:', error);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;

    try {
      await axios.post(`${API}/conversations/${selectedConversation.id}/messages`, null, {
        params: { content: newMessage }
      });
      setNewMessage('');
      fetchMessages(selectedConversation.id);
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message');
    }
  };

  return (
    <div className="container mx-auto px-4 py-8" data-testid="messages-page">
      <h1 className="text-4xl font-bold mb-8">Messages</h1>

      {loading ? (
        <Card>
          <CardContent className="p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          </CardContent>
        </Card>
      ) : conversations.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center space-y-4">
            <div className="text-6xl">💬</div>
            <h3 className="text-2xl font-semibold">No conversations yet</h3>
            <p className="text-muted-foreground">
              Start browsing services and message tradies to begin a conversation
            </p>
            <Button onClick={() => navigate('/browse')} className="rounded-xl">
              Browse Services
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-3 gap-6" style={{ height: '600px' }}>
          {/* Conversations List */}
          <Card className="md:col-span-1">
            <CardContent className="p-4">
              <h2 className="font-semibold mb-4">Conversations</h2>
              <ScrollArea className="h-[500px]">
                <div className="space-y-2">
                  {conversations.map((conv) => (
                    <button
                      key={conv.id}
                      onClick={() => {
                        setSelectedConversation(conv);
                        fetchMessages(conv.id);
                      }}
                      className={`w-full p-3 rounded-xl text-left transition-colors ${
                        selectedConversation?.id === conv.id
                          ? 'bg-primary/10 border border-primary'
                          : 'hover:bg-muted'
                      }`}
                      data-testid={`conversation-${conv.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary/20 to-accent-green/20 flex items-center justify-center flex-shrink-0">
                          <span className="text-lg">
                            {conv.other_user?.role === 'tradie' ? '🔧' : '👤'}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">
                            {conv.other_user?.tradie_profile?.business_name || conv.other_user?.full_name || 'User'}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {new Date(conv.last_message_at).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Messages Thread */}
          <Card className="md:col-span-2">
            <CardContent className="p-6 flex flex-col h-full">
              {selectedConversation ? (
                <>
                  <div className="mb-4 flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary/20 to-accent-green/20 flex items-center justify-center">
                      <span className="text-2xl">
                        {selectedConversation.other_user?.role === 'tradie' ? '🔧' : '👤'}
                      </span>
                    </div>
                    <div>
                      <h2 className="font-semibold">
                        {selectedConversation.other_user?.tradie_profile?.business_name || selectedConversation.other_user?.full_name || 'User'}
                      </h2>
                      <p className="text-xs text-muted-foreground capitalize">
                        {selectedConversation.other_user?.role}
                      </p>
                    </div>
                  </div>
                  <Separator className="mb-4" />

                  {/* Messages */}
                  <ScrollArea className="flex-1 mb-4 h-[400px]">
                    <div className="space-y-4">
                      {/* Show quotes at top */}
                      {quotes.map((quote) => (
                        <Card key={quote.id} className="border-2 border-primary/30 bg-primary/5" data-testid={`quote-card-${quote.id}`}>
                          <CardContent className="p-4 space-y-3">
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-semibold">Quote #{quote.quote_number}</p>
                                <p className="text-sm text-muted-foreground">
                                  {quote.line_items?.length || 0} items • ${quote.total.toFixed(2)}
                                </p>
                              </div>
                              <Badge variant={quote.status === 'accepted' ? 'default' : 'outline'} className={quote.status === 'accepted' ? 'bg-green-500' : ''}>
                                {quote.status === 'sent' && '📤 Sent'}
                                {quote.status === 'viewed' && '👁️ Viewed'}
                                {quote.status === 'accepted' && '✓ Accepted'}
                                {quote.status === 'declined' && '✗ Declined'}
                              </Badge>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              className="w-full"
                              onClick={() => setViewingQuoteId(quote.id)}
                              data-testid={`view-quote-${quote.id}`}
                            >
                              View Quote Details
                            </Button>
                          </CardContent>
                        </Card>
                      ))}
                      
                      {messages.map((msg) => (
                        <div
                          key={msg.id}
                          className={`flex ${msg.sender_id === user.id ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-[70%] rounded-2xl px-4 py-2 ${
                              msg.sender_id === user.id
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-muted'
                            }`}
                          >
                            <p className="text-sm">{msg.content}</p>
                            <span className="text-xs opacity-70 mt-1 block">
                              {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>

                  {/* Safety Banner */}
                  <div className="bg-accent-yellow/10 border border-accent-yellow/30 rounded-xl p-3 mb-4">
                    <div className="flex gap-2 text-sm">
                      <span>🛡️</span>
                      <span className="text-muted-foreground">
                        Keep communications on GoFella. Never share payment details via messages.
                      </span>
                    </div>
                  </div>

                  {/* Message Input */}
                  <div className="space-y-3">
                    {selectedConversation.other_user?.role === 'tradie' && user?.role === 'customer' && (
                      <Button
                        variant="outline"
                        className="w-full rounded-xl border-primary text-primary hover:bg-primary/10"
                        onClick={() => toast.info('Quote builder coming soon!')}
                        data-testid="request-quote-button"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Request Quote
                      </Button>
                    )}
                    
                    {selectedConversation.other_user?.role === 'customer' && user?.role === 'tradie' && (
                      <Button
                        variant="outline"
                        className="w-full rounded-xl border-primary text-primary hover:bg-primary/10"
                        onClick={() => setShowQuoteBuilder(true)}
                        data-testid="create-quote-button"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Send Quote
                      </Button>
                    )}
                    
                    <div className="flex gap-2">
                      <Input
                        placeholder="Type a message..."
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                        className="flex-1 rounded-xl"
                        data-testid="message-input"
                      />
                      <Button
                        onClick={sendMessage}
                        className="rounded-xl bg-primary hover:bg-primary-hover"
                        data-testid="send-message-button"
                      >
                        Send
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-muted-foreground">
                  Select a conversation to view messages
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Quote Builder Dialog */}
      {showQuoteBuilder && selectedConversation && user?.tradie_profile && (
        <QuoteBuilder
          open={showQuoteBuilder}
          onClose={() => {
            setShowQuoteBuilder(false);
            if (selectedConversation) {
              fetchQuotes(selectedConversation.id);
            }
          }}
          customerId={selectedConversation.other_user?.id}
          conversationId={selectedConversation.id}
          tradieProfile={user.tradie_profile}
        />
      )}
      
      {/* Quote Viewer Dialog */}
      {viewingQuoteId && (
        <Dialog open={!!viewingQuoteId} onOpenChange={() => setViewingQuoteId(null)}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Quote Details</DialogTitle>
            </DialogHeader>
            <QuoteViewer quoteId={viewingQuoteId} onClose={() => setViewingQuoteId(null)} />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default MessagesPage;
